import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid';
import { navigationRoutes } from '../constants/constants';
import { Dialog, DialogPanel } from '@headlessui/react';
import { HeaderNavComponent } from './header-nav-compontent';

interface BlogPost {
    id: string; // Assuming posts have an ID for routing
    title: string;
    content: string;
    createdAt: string; // Or Date object
}

interface BlogPageProps {
    posts: BlogPost[];
}

export const BlogPage: React.FC<BlogPageProps> = ({ posts }) => {
    const { postId, postName } = useParams<{ postId?: string; postName?: string }>();
    const location = useLocation();
    const isListPage = !postId; // Determine if we are on the list page or a single post page

    const currentPost = isListPage ? null : posts.find(p => p.id === postId);

    const [searchQuery, setSearchQuery] = useState('');
    const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>(posts);

    useEffect(() => {
        if (isListPage) {
            document.title = 'PocketQuant | Blogs';
        } else if (currentPost) {
            document.title = `PocketQuant | ${currentPost.title}`;
        } else {
            // Optional: Handle case where post ID is in URL but post not found
            document.title = 'PocketQuant | Blog Post Not Found';
        }
        // Reset title when component unmounts or route changes
        return () => {
            document.title = 'PocketQuant'; // Or your default title
        };
    }, [isListPage, currentPost, location.pathname]); // Rerun effect if route changes

    // Filter posts based on search query
    useEffect(() => {
        // If search query is empty, show all posts
        if (searchQuery.trim() === '') {
            setFilteredPosts(posts);
            return;
        }
        const filtered = posts.filter(post =>
            post.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setFilteredPosts(filtered);
    }, [searchQuery, posts]);

    if (!isListPage) {
        // Display single blog post content
        if (!currentPost) {
            return (
                <div className="container mx-auto px-4 py-8">
                    <h1 className="text-3xl font-bold mb-4 text-center">Post Not Found</h1>
                    <Link to="/blogs" className="text-blue-500 hover:underline">
                        &larr; Back to Blog List
                    </Link>
                </div>
            );
        }

        return (
            <div className="container mx-auto px-4 py-8 max-w-4xl">
                <Link to="/blogs" className="text-blue-500 hover:underline mb-6 inline-block">
                    &larr; Back to Blog List
                </Link>
                <article className="prose lg:prose-xl dark:prose-invert max-w-none">
                    <h1 className="text-4xl font-bold mb-2">{currentPost.title}</h1>
                    <time className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                        Published on {currentPost.createdAt} EST
                    </time>
                    <ReactMarkdown>{currentPost.content}</ReactMarkdown>
                </article>
            </div>
        );
    }

    // Display list of blog posts
    return (
        <div className="blog-index-page min-h-screen p-8">
            <HeaderNavComponent variant="product" />
            <div className='flex flex-col md:flex-row md:items-center'>
                <h1 className="text-4xl mt-6 font-bold text-gray-900 mb-2 md:mb-6">The PQ Blog</h1>
                {/* search input */}
                <div className="mt-4 md:mt-0 md:ml-auto sm:flex lg:gap-x-12">
                    <div className="relative flex gap-x-4">
                        <input
                            type="text"
                            placeholder="Search..."
                            className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-600 sm:text-sm"
                            onChange={(e) => setSearchQuery(e.target.value)}
                            value={searchQuery}
                        />
                    </div>
                </div>
            </div>

            <div className="max-w-3xl rounded-xl border-gray-400 p-8">
                <ul className="divide-y divide-gray-400">
                    {filteredPosts.map((post, index) => (
                        <li key={index} className="py-4 cursor-pointer">
                            <a href={`blogs/${post.id}/${post.title}`} className="text-xl font-semibold text-gray-900">{post.title}</a>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};
